// Player page for player joining a room.

import React from 'react';
import { useNavigate } from 'react-router';
import Avatar from '@/components/avatars/Avatar';
import { useAnimalStyleAvatars } from '@/components/avatars/hooks';
import GameInProgressPanel from '@/components/room/GameInProgressPanel';
import PlayerCustomizationPanel from './PlayerCustomizationPanel';
import {
  usePlayerJoinFlow,
  useRoomConfig,
  useRoomMyPlayer,
  useRoomPlayerSelection,
  useRoute,
  useRouteTwitchError,
  useSystemWindowSize,
  usePlayerFaceCustomization,
} from '@/hooks';
import { initiateTwitchAuth } from '@/transport/api/publicApi';
import { analyticsService } from '@/services/analyticsService';
import { createLogger } from '@airhead-games/shared/utilities';
import {
  getColorStop,
  PLAYABLE_FAMILIES,
  resolvePlayerPalette,
  type PlayerColorFamily,
} from '@/styles/playerColors';

const logger = createLogger('Player');

const Player: React.FC = () => {
  const navigate = useNavigate();
  const { width, height } = useSystemWindowSize();
  const { roomCode: urlRoomCode } = useRoute();
  const { status, roomCode: stateRoomCode } = useRoomConfig();
  const roomCode = stateRoomCode || urlRoomCode;
  const isAnimalStyleRoom = useAnimalStyleAvatars();
  const myPlayer = useRoomMyPlayer();
  const { selectPlayerData } = useRoomPlayerSelection();

  useRouteTwitchError();

  const avatarSize = React.useMemo(() => {
    if (width < 360) return 220;
    if (width < 420) return height < 640 ? 220 : 260;
    if (width < 640) return 300;
    if (width < 768) return 340;
    if (width < 1024) return 400;
    return 480;
  }, [height, width]);

  const otherPlayerColors = selectPlayerData({
    property: 'playerColor',
    filterExclude: myPlayer?.playerId ? [myPlayer.playerId] : [],
    filterFrom: 'playerId',
  });
  const availableColors = PLAYABLE_FAMILIES.filter(
    (color) => !otherPlayerColors.includes(color)
  );
  const [selectedColor, setSelectedColor] = React.useState<PlayerColorFamily>(
    (myPlayer?.playerColor as PlayerColorFamily) || PLAYABLE_FAMILIES[0]
  );
  const { customFace, cycleExpression } = usePlayerFaceCustomization({
    initialFace: myPlayer?.face,
    roomCode,
  });
  const { submit, isJoining } = usePlayerJoinFlow({
    roomCode,
    selectedColor,
    customFace,
  });

  React.useEffect(() => {
    if (myPlayer?.playerColor) {
      setSelectedColor(myPlayer.playerColor as PlayerColorFamily);
    }
  }, [myPlayer?.playerColor]);

  const goToGame = React.useCallback(() => {
    analyticsService.trackButtonClick('Go To Game', {
      link_location: 'player_page',
      room_code: roomCode || 'unknown',
      game_status: status,
    });
    if (roomCode) {
      navigate(`/game/${roomCode}/playing`);
    }
  }, [navigate, roomCode, status]);

  const handleColorSelect = React.useCallback(
    (color: PlayerColorFamily) => {
      if (color !== selectedColor) {
        analyticsService.trackButtonClick('Select Color', {
          link_location: 'player_customization',
          room_code: roomCode || 'unknown',
          previous_color: selectedColor,
          new_color: color,
        });
      }
      setSelectedColor(color);
    },
    [roomCode, selectedColor]
  );

  const handleTwitchAuthClick = React.useCallback(() => {
    if (!myPlayer?.playerId || !roomCode) {
      logger.error('Cannot initiate Twitch auth: missing playerId or roomCode');
      return;
    }
    analyticsService.trackButtonClick('Join with Twitch', {
      link_location: 'player_page',
      room_code: roomCode,
    });
    initiateTwitchAuth('player', {
      playerId: myPlayer.playerId,
      roomCode,
    });
  }, [myPlayer?.playerId, roomCode]);

  return (
    <div className="relative flex h-[100svh] w-full max-w-full flex-col items-center justify-start overflow-auto overflow-x-hidden overscroll-none px-4 py-4 text-center select-none sm:py-6 md:justify-center md:py-8">
      {status === 'playing' ? (
        <GameInProgressPanel
          onGoToGame={goToGame}
          topSlot={
            <Avatar
              player={myPlayer ?? undefined}
              emotion="happy"
              customExpression={myPlayer?.face}
              animalStyle={isAnimalStyleRoom}
              size={avatarSize}
              topLabel={myPlayer?.playerName || 'Player'}
              topLabelColor={
                myPlayer?.playerColor
                  ? resolvePlayerPalette(myPlayer).palette.light
                  : getColorStop('neutral', '50')
              }
            />
          }
        />
      ) : (
        <PlayerCustomizationPanel
          avatarSize={avatarSize}
          availableColors={availableColors}
          selectedColor={selectedColor}
          onColorSelect={handleColorSelect}
          customFace={customFace}
          onCycleExpression={cycleExpression}
          isJoining={isJoining}
          onSubmit={submit}
          onTwitchAuthClick={handleTwitchAuthClick}
        />
      )}
    </div>
  );
};

export default Player;
